/**
 * App-wide region/currency context.
 *
 * Why a context (not just `useDefaultRegion`)?  Many components need to
 * react when the user switches their region (header chip, profile settings,
 * product cards). A context lets one provider broadcast the change without
 * each consumer re-fetching.
 *
 * Resolution order (mirrors the server middleware so SSR + client agree):
 *   1. Authenticated user's `defaultCountry`     (from redux `auth.user`)
 *   2. localStorage 'region'                      (last known value)
 *   3. `region` cookie set by /api/region         (server's best guess)
 *   4. GET /api/region                            (one-shot fallback)
 *   5. 'IN'                                       (final default)
 *
 * Public API:
 *   const { region, setRegion, currency, format } = useRegion();
 *     region      - 'IN' | 'US' | 'MV'
 *     setRegion   - function(code) — persists locally; also pushes to server
 *                   when the user is logged in
 *     currency    - 'INR' | 'USD' | 'MVR'
 *     format(n, sourceCurrency='INR')  - returns a localized price string
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import API from './api';
import { COUNTRIES, currencyForCountry, formatMoney } from './region';
import { updateProfile } from '../store/slices/authSlice';

const STORAGE_KEY = 'region';
const ALLOWED = new Set(COUNTRIES.map((c) => c.code));

// Read the cookie set by the server's region middleware. Synchronous so we
// can use it for the first paint without an extra API hop.
const readCookieRegion = () => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/(?:^|;\s*)region=([^;]+)/);
  const code = m ? decodeURIComponent(m[1]).toUpperCase() : null;
  return code && ALLOWED.has(code) ? code : null;
};

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && ALLOWED.has(v) ? v : null;
  } catch {
    return null;
  }
};

const writeStored = (code) => {
  try { localStorage.setItem(STORAGE_KEY, code); } catch { /* private mode */ }
};

const RegionContext = createContext({
  region: 'IN',
  currency: 'INR',
  setRegion: () => {},
  format: (n) => String(n),
});

export const RegionProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.user);

  // First-paint best guess: user.defaultCountry > localStorage > cookie > 'IN'.
  // We update further once /api/region resolves if no other signal exists.
  const [region, setRegionState] = useState(() => {
    return user?.defaultCountry || readStored() || readCookieRegion() || 'IN';
  });

  // When the logged-in user changes (login / logout / profile reload),
  // re-sync to their stored preference so device changes still respect it.
  useEffect(() => {
    if (user?.defaultCountry && ALLOWED.has(user.defaultCountry)) {
      setRegionState(user.defaultCountry);
      writeStored(user.defaultCountry);
    }
  }, [user?.defaultCountry]);

  // One-shot server-side detection if we still have nothing stored locally.
  // Silent — failure just leaves the default in place.
  useEffect(() => {
    if (readStored() || user?.defaultCountry) return;
    let cancelled = false;
    API.get('/region', { silent: true })
      .then(({ data }) => {
        if (cancelled) return;
        const code = String(data?.region || '').toUpperCase();
        if (ALLOWED.has(code)) {
          setRegionState(code);
          writeStored(code);
        }
      })
      .catch(() => { /* keep current */ });
    return () => { cancelled = true; };
  }, [user?.defaultCountry]);

  const setRegion = useCallback((code) => {
    const cc = String(code || '').toUpperCase();
    if (!ALLOWED.has(cc)) return;
    setRegionState(cc);
    writeStored(cc);
    // Best-effort persist on the user record so other devices see it.
    if (user?._id && user?.defaultCountry !== cc) {
      dispatch(updateProfile({ defaultCountry: cc }));
    }
  }, [user, dispatch]);

  const value = useMemo(() => ({
    region,
    currency: currencyForCountry(region),
    setRegion,
    format: (amount, sourceCurrency = 'INR') => formatMoney(amount, region, sourceCurrency),
  }), [region, setRegion]);

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = () => useContext(RegionContext);
